/**
 * Simple in-memory GET cache for axios responses.
 * - Caches successful GET responses by URL+params
 * - Configurable TTL per endpoint pattern
 * - Mutations (POST/PUT/DELETE) invalidate related caches
 * - Manual invalidation via invalidate(pattern)
 */

const cache = new Map();

// TTL rules: longest-match pattern wins
const TTL_RULES = [
  // Stable context data — 2 min cache
  { pattern: '/auth/me', ttl: 120_000 },
  { pattern: '/plan-limits', ttl: 120_000 },
  { pattern: '/warehouses', ttl: 120_000 },  // warehouse LIST (not inventory)
  { pattern: '/onboarding/context', ttl: 120_000 },
  { pattern: '/onboarding/tour-state', ttl: 120_000 },
  { pattern: '/uom-options', ttl: 300_000 },

  // List data — 30s cache (fresh enough for back-nav)
  { pattern: '/warehouse/inventory', ttl: 30_000 },
  { pattern: '/purchase-orders', ttl: 30_000 },
  { pattern: '/items', ttl: 30_000 },
  { pattern: '/vans', ttl: 30_000 },
  { pattern: '/vendors', ttl: 30_000 },
  { pattern: '/departments', ttl: 30_000 },
  { pattern: '/item-categories', ttl: 60_000 },
  { pattern: '/part-requests/pending-count', ttl: 30_000 },
];

function getTTL(url) {
  for (const rule of TTL_RULES) {
    if (url.includes(rule.pattern)) return rule.ttl;
  }
  return 0; // no cache by default
}

function cacheKey(config) {
  const url = config.url || '';
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${url}|${params}`;
}

/**
 * Axios request interceptor: serve from cache if fresh.
 * Attach to axios instance: api.interceptors.request.use(apiCache.requestInterceptor)
 */
export function requestInterceptor(config) {
  if (config.method !== 'get' || config.noCache) return config;

  const key = cacheKey(config);
  const entry = cache.get(key);
  if (entry && Date.now() - entry.ts < entry.ttl) {
    // Return cached response via a cancel + adapter trick
    config._fromCache = true;
    config._cachedData = entry.data;
    config.adapter = () =>
      Promise.resolve({
        data: entry.data,
        status: 200,
        statusText: 'OK (cached)',
        headers: {},
        config,
      });
  }
  return config;
}

/**
 * Axios response interceptor: store successful GETs.
 */
export function responseInterceptor(response) {
  const config = response.config;
  if (config.method !== 'get' || config._fromCache) return response;

  const url = config.url || '';
  const ttl = getTTL(url);
  if (ttl > 0) {
    const key = cacheKey(config);
    cache.set(key, { data: response.data, ts: Date.now(), ttl });
  }
  return response;
}

/**
 * Invalidate cached entries matching a pattern.
 * Call after mutations: apiCache.invalidate('/purchase-orders')
 */
export function invalidate(pattern) {
  for (const [key] of cache) {
    if (key.includes(pattern)) {
      cache.delete(key);
    }
  }
}

/**
 * Invalidate all cache entries.
 */
export function invalidateAll() {
  cache.clear();
}

// Phase 6: any successful write can change stock, PO status or counts shown on other screens
// (e.g. receiving a PO changes warehouse and vehicle inventory), so drop all short-lived list
// entries and tell mounted pages to revalidate. Long-lived context data (auth, plan) is kept.
export const DATA_CHANGED_EVENT = 'sl:data-changed';
export function onMutationSuccess(config) {
  for (const [key, val] of cache) {
    if (val.ttl <= 60_000) cache.delete(key);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT, { detail: { url: config?.url || '', method: config?.method } }));
  }
}

/** For debugging */
export function getCacheStats() {
  const entries = [];
  for (const [key, val] of cache) {
    entries.push({
      key,
      age_ms: Date.now() - val.ts,
      ttl: val.ttl,
      fresh: Date.now() - val.ts < val.ttl,
    });
  }
  return { size: cache.size, entries };
}
